import { Component, OnInit, ElementRef, OnDestroy, ChangeDetectorRef, SimpleChanges, inject, DestroyRef, signal, Inject, PLATFORM_ID  } from '@angular/core';
import {Router, ActivatedRoute, NavigationExtras, Params} from '@angular/router';
import { CommonService } from '../common.service';
import { ConfigService } from '../config.service';
import {Location, isPlatformBrowser} from '@angular/common';
import { EMPTY, Subscription, SubscriptionLike, catchError, map } from 'rxjs';
import {GoogleAnalyticsService} from '../google-analytics.service';
import {FilterData} from "./Interfaces";
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { DownloadService } from "../download-service"
import { PlatformService } from '../platform.service';
import { DownloadOption } from './../entry/entry-dropdown/entry-dropdown.component';
import { ThresholdNumberArrayGenerator } from 'd3-array';
import { IPSAE_MIN_LOWER_LIMIT, PDOCKQ2_MIN_LOWER_LIMIT, INTERFACE_SCORE_COLORS } from '../constants';
import { PortalCategory, DEFAULT_CATEGORIES } from '../portal-categories';

interface FilterKeyMap {
  [key: string]: keyof SearchComponent['selectedFilters'];
}

@Component({
    selector: 'app-search',
    templateUrl: './search.component.html',
    styleUrls: ['./search.component.css'],
    standalone: false
})
export class SearchComponent implements OnInit, OnDestroy {
  
  private readonly filterKeyMap: FilterKeyMap = {
    ipTM: 'complexPredictionAccuracy_ipTM',
    ipSAE: 'complexPredictionAccuracy_ipSAE',
    pDockQ2: 'complexPredictionAccuracy_pDockQ2',
    LIS: 'complexPredictionAccuracy_LIS'
  };

  searchField: string;
  searchTerm: string;
  sortBy: string = "HSP score: highest first";
  selectedFilters: {
    organismScientificName: string[],
    isUniProtReviewed: string[],
    isUniProtReferenceProteome: string[],
    globalMetricValue: any,
    complexPredictionAccuracy_ipTM: any,
    complexPredictionAccuracy_ipSAE: any,
    complexPredictionAccuracy_pDockQ2: any,
    complexPredictionAccuracy_LIS: any,
    oligomericStateDescription: string[]
    providerId: string[]
  };
  // filters: string[];
  filters: {
    isUniProtReviewed: string,
    isUniProtReferenceProteome: string,
    common_organisms: string,
    organismScientificName: string,
    globalMetricValue: any,
    complexPredictionAccuracy_ipTM: any,
    complexPredictionAccuracy_ipSAE: any,
    complexPredictionAccuracy_pDockQ2: any,
    complexPredictionAccuracy_LIS: any,
    oligomericStateDescription: string,
    providerId: string
  };
  filterData: FilterData;
  filterLabels: any;
  mainResultData: any;
  paginationData: any;
  loadComplete: boolean;
  routeSubscriber: Subscription;
  locSubscriber: SubscriptionLike;
  isFilterListOpen: boolean;
  isSuggested: boolean;
  queryParams: Params;
  isRangeFilterResultsEmpty: boolean =  false;

  minplddt: number = 0;
  maxplddt: number = 100;

  minInterface: number = 0;
  maxInterface: number = 1;

  subTimeout:any;
  isSequenceSearch: boolean = false;
  isSearchprogress: boolean = false;
  isNoresult: boolean = false;
  isCopySequence: boolean = false;
  isCopyLink: boolean = false;
  localStorageSearchTerm: string;
  
  selectedItems: boolean[] = []; // Array to track selection state for each item

  public dropdownOptionsFilter: DownloadOption[] = [];
  public dropdownSelectedFilter: string;
  public tooltipTextMap:string;
  readonly MAX_SELECTION = 100;
  readonly PAGE_SIZE = 20;
  isLimitReached = false;

  pageSelections: { [key: number]: boolean[] } = {}; // Tracks selections per page (e.g., {0: [true, false, ...]})
  currentPage = 0;

  private readonly downloadService = inject(DownloadService);
  selectedValues = this.downloadService.selectedValues;
  allSelected= this.downloadService.allSelected;

  errorEntryText: any;
  hashedUrl: any;
  private readonly destroyRef = inject(DestroyRef);
  isLatestRoute: boolean = false;
  // Set when the route's field/term (e.g. thematic_tags/PPP) matches a known
  // portal category (see resolvePortalCategory), to show that category's
  // title/description/accent-colour banner above the results.
  activePortalCategory: PortalCategory | undefined;
  public oligomericStateDescription = 'initial';
  public providerId = 'initial'

  constructor(
    private location: Location,
    private commonService: CommonService,
    private route: ActivatedRoute,
    private router: Router,
    private configService: ConfigService,
    private el: ElementRef,
    private http: HttpClient,
    private changeDetectorRef: ChangeDetectorRef,
    public gaService: GoogleAnalyticsService,
    private platform : PlatformService,
    @Inject(PLATFORM_ID) private platformId: Object) {}
    dropdownOptions: { label: string, value: any, isShow: boolean, disabled?: boolean, info?: string }[];


  ngOnInit(): void {
    const routeData = this.route.snapshot.data;
    this.isLatestRoute = !!routeData['isLatest'];
    if (this.isLatestRoute) {
      this.searchField = 'latest';
      this.searchTerm = '';
    }

    this.updateDropdown();
    this.dropdownSelectedFilter = "ipSAE";
    this.tooltipTextMap = "Interaction prediction score from aligned errors (ipSAE) focuses on inter-chain residue pairs with more reliable predicted positioning, making it less sensitive to disordered or non-interacting regions.";
    
    this.selectedItems = new Array(this.PAGE_SIZE).fill(false);
    this.dropdownOptions = [
      { label: 'Search results (max 10 000)', value: "allresultscsv" , isShow: true, disabled: false },
      { label: 'Coordinates and PAE (max 100)', value: "modelcoordicate", isShow: true, disabled: true },
    ];

    this.loadComplete = false;
    this.filterLabels = {
      isUniProtReviewed: 'Review',
      isUniProtReferenceProteome: 'Reference proteome',
      common_organisms: 'Popular',
      organismScientificName: 'Other organisms',
      globalMetricValue: 'Average pLDDT score',
      complexPredictionAccuracy_ipTM: 'Interface score',
      complexPredictionAccuracy_ipSAE: 'Interface score',
      complexPredictionAccuracy_pDockQ2: 'Interface score',
      complexPredictionAccuracy_LIS: 'Interface score',
      providerId: 'Dataset'
    };

    this.filters = {
      isUniProtReviewed: 'isUniProtReviewed',
      isUniProtReferenceProteome: 'isUniProtReferenceProteome',
      common_organisms: 'organismScientificName',
      organismScientificName: 'organismScientificName',
      globalMetricValue: 'globalMetricValue',
      complexPredictionAccuracy_ipTM: 'complexPredictionAccuracy_ipTM',
      complexPredictionAccuracy_ipSAE: 'complexPredictionAccuracy_ipSAE',
      complexPredictionAccuracy_pDockQ2: 'complexPredictionAccuracy_pDockQ2',
      complexPredictionAccuracy_LIS: 'complexPredictionAccuracy_LIS',
      oligomericStateDescription: 'oligomericStateDescription',
      providerId: 'providerId'
    };

    // --- Query Params Subscription ---
    this.route.queryParams.subscribe(queryParams => {
      this.initResultData();
      this.queryParams = queryParams;

      // --- Global Metric Logic (pLDDT) ---
      if (this.queryParams.globalMetricValue) {
        const { min, max } = this.extractMinMax(this.queryParams.globalMetricValue);
        this.minplddt = min;
        this.maxplddt = max;
      } else {
        this.minplddt = 0;
        this.maxplddt = 100;
      }

      // --- Optimized Accuracy Keys Logic ---
      const accuracyKeys = [
        'complexPredictionAccuracy_ipTM',
        'complexPredictionAccuracy_ipSAE',
        'complexPredictionAccuracy_pDockQ2',
        'complexPredictionAccuracy_LIS'
      ];

      this.maxInterface = 1;

      const activeKey = accuracyKeys.find(key => this.queryParams[key]);
      if (activeKey) {
        const { min, max } = this.extractMinMax(this.queryParams[activeKey]);
        this.minInterface = min;
        this.maxInterface = max;
      } else {
        this.minInterface = this.interfaceMinLimit;
      }

      // Update internal state from URL
      this.transformQueryParamsToSelectedFilters(queryParams);
      this.changeDetectorRef.detectChanges();

      // Trigger API call if we are already on the page (prevents double call on first load)
      if (this.loadComplete) {
        this.executeSearch();
      }
    });

    // --- Route Params Subscription (Main Entry Point) ---
    this.routeSubscriber = this.route.params.subscribe(params => {
      this.loadComplete = false;
      this.initResultData();
      this.searchField = params.field || (this.isLatestRoute ? 'latest' : this.searchField);
      // Portal category URLs use the readable 'thematic-tag' browser segment (see
      // PortalCardComponent's routerLink), not the backend's actual field name
      // ('thematic_tags'). Normalize it here, once, so every other use of
      // this.searchField below (resolvePortalCategory, the actual API query,
      // filters, etc.) keeps working unchanged with the real backend field name,
      // exactly as it already does for every other field.
      if (this.searchField === 'thematic-tag') {
        this.searchField = 'thematic_tags';
      }
      const routeTerm = params.term ? this.replaceEscapedSlash(params.term) : (this.isLatestRoute ? '' : this.searchTerm);
      // Portal category URLs show a readable slug (e.g. thematic-tag/pandemic-preparedness)
      // rather than the backend's raw tag code (PPP) — resolve it here, once, so every
      // other use of searchTerm below (the actual API query, filters, etc.) keeps working
      // with the real backend value exactly as it already does for every other field.
      this.activePortalCategory = this.resolvePortalCategory(this.searchField, routeTerm);
      this.searchTerm = this.resolveSearchTerm(routeTerm, this.activePortalCategory);
      this.processQueryParams(this.location.path());
      this.executeSearch();
    });

    if(this.platform.isBrowserPlatform()){
      const platformsessionStorage = this.platform.getSessionStorage();
      if(platformsessionStorage[this.searchTerm]){
        const ids = JSON.parse(platformsessionStorage.getItem(this.searchTerm));
        this.downloadService.updateSelectedValueSearch(ids, this.searchTerm);
      } else {
        this.downloadService.updateSelectedValueSearch([], this.searchTerm);
      }
    }  
    
    // Subscribe to location for 'Back' button functionality
    this.locSubscriber = this.location.subscribe(params => {
      this.loadComplete = false;
      this.initResultData();
      this.processQueryParams(params.url);
      this.executeSearch();
    });
  }

  /** The minimum selectable lower bound for the Interface quality score slider.
   * Returns IPSAE_MIN_LOWER_LIMIT (0.6) for ipSAE, PDOCKQ2_MIN_LOWER_LIMIT (0.23) for pDockQ2, otherwise 0. */
  get interfaceMinLimit(): number {
    if (this.dropdownSelectedFilter === 'ipSAE') {
      return IPSAE_MIN_LOWER_LIMIT;
    }
    if (this.dropdownSelectedFilter === 'pDockQ2') {
      return PDOCKQ2_MIN_LOWER_LIMIT;
    }
    return 0;
  }

  get isEntrySelected() : boolean {
    const selected = this.selectedItems.filter(isSelected => isSelected).length;
    return selected > 0 ? false : true ;
  }
  get selectedCount(): number {
    return this.selectedItems.filter(isSelected => isSelected).length;
  }

  ngOnChanges(): void {
    this.updateDropdown()
  }

  public async onDropdownSelectFilter(event: string) {
     
    if(event === "ipSAE"){
      this.tooltipTextMap = "Interaction prediction score from aligned errors (ipSAE) focuses on inter-chain residue pairs with more reliable predicted positioning, making it less sensitive to disordered or non-interacting regions."
    }
    if(event === "ipTM"){
    this.tooltipTextMap = "Interface predicted TM-score (ipTM) measures confidence in the predicted relative positions of the protein chains in a complex."
    }
    if(event === "pDockQ2"){  
      this.tooltipTextMap = "Estimates the expected quality of an interface by combining local residue confidence with confidence in the predicted relative positioning across the interface."  
    }
    if(event === "LIS"){
      this.tooltipTextMap = "Local Interaction Score (LIS) is derived from low PAE regions and reflects local confidence in the predicted interaction interface."
    } 
    this.dropdownSelectedFilter = event;
    this.minInterface = this.interfaceMinLimit;
    this.maxInterface = 1;
  }

  updateDropdown() {
    this.dropdownOptionsFilter = [
      {
        name: `ipSAE`,
        url: '',
        downloadable: false,
      },
      {
        name: "ipTM",
        url: '',
        downloadable: false,
      },
       {
        name: `pDockQ2`,
        url: '',
        downloadable: false,
      },
       {
        name: `LIS`,
        url: '',
        downloadable: false,
      }

    ];
     this.dropdownSelectedFilter = "ipTM";
    // this.dropdownSelected = `Domain ${this.activeDomain().tedNumber}`;
  }

  isAllSelectedAFDB(): boolean {
    const startIndex = 0;
    const endIndex = 20;
    const visibleRows = this.mainResultData?.slice(startIndex, endIndex);
    const isallselectedafdb = visibleRows?.every(row => this.selectedValues().includes(row.modelEntityId+'-v'+row.latestVersion));
    this.downloadService.updateAllSelected(isallselectedafdb);
    return isallselectedafdb; // Check by ID
  }
  // Toggle Select All
  toggleSelectAll(event: any) {
    // this.allSelected = event.target.checked;
    this.downloadService.updateAllSelected(event.target.checked);
    if (!this.pageSelections[this.currentPage]) {
        this.initializePageSelections(this.currentPage);
    }
    // Loop through current page entries to set selection
    
    this.pageSelections[this.currentPage].forEach((_, index) => {
      if(this.mainResultData.length > index){
        const itemValue = this.mainResultData[index].modelEntityId + "-v" + this.mainResultData[index].latestVersion;
        
        if (this.allSelected() && this.selectedValues().length < this.MAX_SELECTION) {
          this.pageSelections[this.currentPage][index] = true;
          // let selectedValues = [];
          if (!this.selectedValues().includes(itemValue)) {
              const selectedValues = [...this.selectedValues(), itemValue];
              this.downloadService.updateSelectedValueSearch(selectedValues, this.searchTerm);
          }
          const selectedValues = this.selectedValues();
         
          this.downloadService.updateSelectedValueSearch(selectedValues, this.searchTerm);

        } else {
          this.pageSelections[this.currentPage][index] = false;
          const selectedValues = this.selectedValues().filter(value => value !== itemValue);
          this.downloadService.updateSelectedValueSearch(selectedValues, this.searchTerm);
        }
      }
    });
    this.isAllSelectedAFDB();
    this.checkSelectionLimit();
  }

  onItemSelectionChanged(event: { value: string, selected: boolean }, index: number) {
    // Ensure pageSelections for the current page is initialized
    if (!this.pageSelections[this.currentPage]) {
      this.initializePageSelections(this.currentPage);
    }
    this.pageSelections[this.currentPage][index] = event.selected;
    if (event.selected) {
      if (!this.selectedValues().includes(event.value) && this.selectedValues().length < this.MAX_SELECTION) {
        const selectedValues = [...this.selectedValues(), event.value]
        this.downloadService.updateSelectedValueSearch(selectedValues, this.searchTerm);
        this.checkSelectionLimit();
      }
    } else {
      const selectedValues = this.selectedValues().filter(value => value !== event.value);
      this.downloadService.updateSelectedValueSearch(selectedValues, this.searchTerm);
    }
    this.changeDetectorRef.detectChanges();
    const allSelected = this.pageSelections[this.currentPage].every(selected => selected);
    // this.downloadService.updateAllSelected(allSelected);
    this.isAllSelectedAFDB();
    this.checkSelectionLimit();
  }

  checkSelectionLimit() {
    this.isLimitReached = this.selectedValues().length >= this.MAX_SELECTION;
  }

  clearSelection() {
    this.downloadService.updateAllSelected(false);
    Object.keys(this.pageSelections).forEach(page => this.pageSelections[page].fill(false));
    
    this.downloadService.updateSelectedValueSearch([], this.searchTerm);
    this.isLimitReached = false;
    this.selectedItems.fill(false);
    this.changeDetectorRef.detectChanges();
  }

  handleSelection(downloadType: string) {
    const auth = this.configService.getConfig().apiKey ? `&key=${this.configService.getConfig().apiKey}` : '';
    let allcsvapiurl: string;
    if (this.isLatestRoute) {
      allcsvapiurl = this.buildLatestApiUrl({ type: 'main', start: 0, rows: 10000, format: 'csv' }) + auth;
    } else {
      const queryTerms = this.getQueryTerms();
      const fqString = queryTerms.fq.length > 0 ? `&fq=${queryTerms.fq.join('%20AND%20')}` : '';
      allcsvapiurl = `${this.configService.getConfig().searchApiUrl}?q=${queryTerms.q}${fqString}&type=main&start=0&rows=10000`+auth+'&format=csv';
      if(this.isSequenceSearch){
        allcsvapiurl = `${this.configService.getConfig().searchApiUrl}?q=${this.searchTerm.replace(/\s+/g, '')}${fqString}&type=sequence&start=0&rows=10000`+auth+'&format=csv';
      }
    }
    const urlMap = {
      allresultscsv: {url: allcsvapiurl, tag: "search_download_results", msg: "Clicks on download Results table in search"},
      modelcoordicate: {url: allcsvapiurl, tag: "search_download_coordinates", msg: "Clicks on download All model data (coordinates and PAE) in search"}
    };

    const downloadUrl = urlMap[downloadType].url;
    // /latest has no real search term (it's ''), so the CSV filename would otherwise be
    // "-results-csv.csv" — use "latest" instead just for the downloaded file's name.
    const downloadSearchTerm = this.isLatestRoute ? 'latest' : this.searchTerm;
    this.downloadService.initiateDownload(downloadUrl, downloadType, downloadSearchTerm, this.selectedValues(), this.isSequenceSearch, "search_page");
    this.gaService.eventEmitter('search_download', 'search_results', 'click',  urlMap[downloadType].tag,  urlMap[downloadType].msg);
  }

  closeToast(): void {
    this.clearSelection();
  }

  gradientColors = [
    { percentage: 0, color: '#FF7E46' },
    { percentage: 21.35, color: '#FF7E46' },
    { percentage: 29.17, color: '#FFDC14' },
    { percentage: 52.08, color: '#FFDC14' },
    { percentage: 64.58, color: '#66CCF4' },
    { percentage: 79.61, color: '#66CCF4' },
    { percentage: 89.58, color: '#4D96D0' },
    { percentage: 100, color: '#3C70B7' }
  ];

  updateSlider() {
    if (this.minplddt > this.maxplddt) {
      this.minplddt = this.maxplddt - 1;
    } else if (this.maxplddt < this.minplddt) {
      this.maxplddt = this.minplddt + 1;
    }
  }

  updateSliderInterface() {
    if (this.minInterface > this.maxInterface) {
      this.minInterface = this.maxInterface - 1;
    } else if (this.maxInterface < this.minInterface) {
      this.maxInterface = this.minInterface + 1;
    }
  }

  getSliderBackground(): string {
    const leftPercentage = this.minplddt;
    const rightPercentage = this.maxplddt;

    let activeRange = this.getActiveGradient(leftPercentage, rightPercentage);
    return `linear-gradient(90deg, ${activeRange})`;
  }

  getActiveGradient(left: number, right: number): string {
    
    let gradient = this.gradientColors
      .filter(colorStop => colorStop.percentage >= left && colorStop.percentage <= right)
      .map(colorStop => `${colorStop.color} ${colorStop.percentage}%`)
      .join(', ');

    return `gray 0%, gray ${left}%, ${gradient}, gray ${right}%, gray 100%`;
  }

  //update the below funciton to inital state of the slider, which should be completely #5D3F87 and when the slider is moved the selected part should be #5D3F87 and the not selected part should be in gray
  getSliderBackgroundInterface(): string {
    const minLimit = this.interfaceMinLimit;
    const range = 1 - minLimit;
    const leftPercentage = range === 0 ? 0 : ((this.minInterface - minLimit) / range) * 100;
    const rightPercentage = range === 0 ? 100 : ((this.maxInterface - minLimit) / range) * 100;

    let activeRange = this.getActiveGradientInterface(leftPercentage, rightPercentage);
    return `linear-gradient(90deg, ${activeRange})`;
  }

  get interfaceTrackColor(): string {
    return INTERFACE_SCORE_COLORS[this.dropdownSelectedFilter] ?? '#5D3F87';
  }

  //update the below function to update the interface slider, the not selected part should be in gray and the selected part should be #5D3F87
   getActiveGradientInterface(left: number, right: number): string {
    const color = this.interfaceTrackColor;
    return `gray 0%, gray ${left}%, ${color} ${left}%, ${color} ${right}%, gray ${right}%, gray 100%`;
  }

  extractMinMax(rangeStr: string): { min: number, max: number } {
    const cleanStr = rangeStr?.replace(/[\[\]]/g, '');
  
    const parts = cleanStr?.split('+TO+');
  
    if (parts?.length === 2) {
      const min = parseFloat(parts[0]);
      const max = parseFloat(parts[1]);
      return { min, max };
    }
  
    //throw new Error("Invalid range format");
  }

  validateNumericInput(event: any) {
    const input = event.target.value;
    event.target.value = input.replace(/[^0-9]/g, ''); // Ensures only digits remain
  }

  initResultData(): void {
    this.isFilterListOpen = true;
    this.selectedFilters = {
      organismScientificName: [],
      isUniProtReviewed: [],
      isUniProtReferenceProteome: [],
      globalMetricValue: [],
      complexPredictionAccuracy_ipTM: [],
      complexPredictionAccuracy_ipSAE: [],
      complexPredictionAccuracy_pDockQ2: [],
      complexPredictionAccuracy_LIS: [],
      oligomericStateDescription: [],
      providerId: []
    };
    this.paginationData = {
      perPage: 20, currentPage: 1, totalPages: 0, pages: [], totalRecords: 0
    };
  }

  processQueryParams(path: string): void {
    //this.downloadService.updateSelectedValueSearch([]);
    if(this.platform.isBrowserPlatform()){
      const platformsessionStorage = this.platform.getSessionStorage();
      if(platformsessionStorage[this.searchTerm]){
        const ids = JSON.parse(platformsessionStorage.getItem(this.searchTerm));
        this.downloadService.updateSelectedValueSearch(ids, this.searchTerm);
        //this.isAllSelectedAFDB();
      }else{
        this.downloadService.updateSelectedValueSearch([], this.searchTerm);
        this.downloadService.updateAllSelected(false);
      }
    }
   this.isLimitReached = false;
    const urlParamsStr = path.split('?')[1];
    if (urlParamsStr) {
      const paramsList = urlParamsStr.split('&');
      for (const paramStr of paramsList) {
        const paramArr = paramStr.split('=');
        if (paramArr[0] === 'page') {
          this.paginationData.currentPage = +(paramArr[1].trim());
        } else if (paramArr[0] === 'suggested') {
          this.isSuggested = true;
        // } else if (this.filters.indexOf(paramArr[0]) > -1) {
        //   this.selectedFilters[paramArr[0]].push(decodeURIComponent(paramArr[1]).trim());
        // }
        } else if (Array.isArray(this.selectedFilters[paramArr[0]])) {
          this.selectedFilters[paramArr[0]].push(decodeURIComponent(paramArr[1]).trim());
        }
      }
    }
  }

  transformQueryParamsToSelectedFilters(queryparams) {
    const selectedFilters = {};
    for (const [key, value] of Object.entries(queryparams)) {
      if (key  !== 'page' ) {
        this.selectedFilters[key] = [value];
      }
    }
    if(this.queryParams.globalMetricValue !== ""){
      const rangeStr = this.queryParams.globalMetricValue;
      if(rangeStr){
        const { min, max } = this.extractMinMax(rangeStr);
        this.minplddt = Number(min);
        this.maxplddt = Number(max);
      }
      this.changeDetectorRef.detectChanges();
    }
     if(this.queryParams.complexPredictionAccuracy_ipTM !== ""){
      const rangeStr = this.queryParams.complexPredictionAccuracy_ipTM;
      if(rangeStr){
        const { min, max } = this.extractMinMax(rangeStr);
        this.minInterface = Number(min);
        this.maxInterface = Number(max);
      }
      this.changeDetectorRef.detectChanges();
    }
  }

  // Function to clean/encode special characters in the URL
  fixedEncodeURIComponent(str: string): string {
    return encodeURIComponent(str).replace(/[!'()*]/g, function(c) {
      return '%' + c.charCodeAt(0).toString(16);
    });
  }

  fixedEncodeURIComponentForPlddt(str: string): string {
    return encodeURIComponent(str)
      .replace(/[!'()*]/g, function(c) {
        return '%' + c.charCodeAt(0).toString(16);
      })
      .replace(/%2B/g, '+'); // Preserve the plus sign
  }

  escapeSplChars(str: string): string {
    return str.replace(/(!|"|\&\&|\|\||\{|\}|\[|\]|\^|\~|\*|\+|\-|\/|\\|\?|\(|\)|:)/g, function($1, $2) {
      return '\\' + $2;
    });
  }

  escapeSpaces(str: string): string {
    return str.replace(/\s+/g, function($1, $2) {
      return '\\' + $1;
    });
  }

  escapeSlashChars(str: string): string {
    return str.replace(/\/+/g, function($1, $2) {
      return '\\' + $2;
    });
  }

  replaceEscapedSlash(str: string): string {
    return str.replace(/\\[0-9]{2}/g, '/');
  }

  checkUniprotAccession(term: string): boolean {
    // working reg ex without isoform
    //const regCheckUniProtKBAccession = /^([OPQ][0-9][A-Z0-9]{3}[0-9]|[A-NR-Z][0-9](?:[A-Z][A-Z0-9]{2}[0-9]){1,2})$/i;
    const regCheckUniProtKBAccession =
    /^([OPQ][0-9][A-Z0-9]{3}[0-9]|[A-NR-Z][0-9](?:[A-Z][A-Z0-9]{2}[0-9]){1,2})(?:-\d+)?$/i;
    const isUniprotAccession = regCheckUniProtKBAccession.test(term);
    return isUniprotAccession;
  }
  
  cleanParamValue(searchField: string, searchTerm: string): string {
    let cleanVal = (searchTerm ?? '').trim();

    // if(/\s+/g.test(cleanVal)){
      // if containes replace space with * and remove quotes
      if(searchField !== 'globalMetricValue' && searchField !== 'complexPredictionAccuracy_ipTM' && searchField !== 'complexPredictionAccuracy_ipSAE' && searchField !== 'complexPredictionAccuracy_pDockQ2' && searchField !== 'complexPredictionAccuracy_LIS'){
        cleanVal = this.escapeSplChars(cleanVal);
        cleanVal = this.escapeSpaces(cleanVal);
      }
    // }
    if(searchField === 'text' && this.isSuggested) {
      // cleanVal = `"${cleanVal}"`;
      cleanVal = `${searchField}:"${cleanVal}"`;
    }else if(searchField === 'text' && !this.isSuggested) {
      if(/\s+/g.test(cleanVal)){
        cleanVal = `${searchField}:${cleanVal}`;
      } 
      else if(this.checkUniprotAccession(searchTerm))
      {
        cleanVal = `(${searchField}:${cleanVal}*)`;
      }
      else {
        cleanVal = `(${searchField}:*${cleanVal} OR ${searchField}:${cleanVal}*)`;
      }
    } else if(searchField === 'globalMetricValue' || searchField === 'complexPredictionAccuracy_ipTM' || searchField === 'complexPredictionAccuracy_ipSAE' || searchField === 'complexPredictionAccuracy_pDockQ2' || searchField === 'complexPredictionAccuracy_LIS'){
      cleanVal = `${searchField}:${cleanVal}`;
    } 
    else {
      cleanVal = `${searchField}:${cleanVal}`;
    }
   
    if(searchField === 'globalMetricValue' || searchField === 'complexPredictionAccuracy_ipTM' || searchField === 'complexPredictionAccuracy_ipSAE' || searchField === 'complexPredictionAccuracy_pDockQ2' || searchField === 'complexPredictionAccuracy_LIS'){
      return this.fixedEncodeURIComponentForPlddt(cleanVal);
    }else {
      return this.fixedEncodeURIComponent(cleanVal);
    }
   
  }

  getMultimerCount(): number {
    const state = this.filterData?.[0]?.facet_fields?.oligomericStateDescription;
    if (!state) return 0;
    return Object.entries(state)
      .filter(([key]) => key !== 'Monomer')
      .reduce((sum, [, val]) => sum + (val as number), 0);
  }

  getQueryTerms(): any {
    // /api/latest already scopes results to the latest release server-side, so no
    // base q is needed — only fq (facet filters) is used, built below.
    const searchTerm = this.isLatestRoute
      ? ''
      : this.cleanParamValue(this.searchField, this.searchTerm);

    const queryTerms = {
      q: searchTerm,
      fq: []
    };
    // Iterate through selectedFilters directly to avoid duplicate processing
    for (const [key, value] of Object.entries(this.selectedFilters)) {
      if (Array.isArray(value) && value.length > 0) {
        value.forEach(filterVal => {
          if (filterVal.startsWith('!')) {
            // Negation filter: -field:value (e.g. !Monomer → -oligomericStateDescription:Monomer)
            const negVal = filterVal.substring(1);
            const fcSearchTerm = '-' + this.cleanParamValue(key, negVal);
            queryTerms.fq.push(fcSearchTerm);
          } else {
            const fcSearchTerm = this.cleanParamValue(key, filterVal);
            queryTerms.fq.push(fcSearchTerm);
          }
        });
      }
    }
    return queryTerms;
  }

  /** Builds an /api/latest URL. No q is ever sent (the endpoint already scopes to the
   * latest release); fq is included when facet filters are selected, same as /api/search. */
  buildLatestApiUrl(options: { type: 'main' | 'multipleFacet'; start: number; rows: number; format?: 'csv' }): string {
    const { fq } = this.getQueryTerms();
    const fqString = fq.length > 0 ? `&fq=${fq.join('%20AND%20')}` : '';
    const latestBaseURL = this.configService.getConfig().latestApiUrl;
    const formatString = options.format ? `&format=${options.format}` : '';
    return `${latestBaseURL}?type=${options.type}&start=${options.start}&rows=${options.rows}${fqString}${formatString}`;
  }

  getAPIResults(searchApiUrl, auth, type, metaData) {
    if(metaData.searchTerm !== this.searchTerm) return;
  
    // Request API data
    this.commonService.getApiData(searchApiUrl + auth, metaData).subscribe(apiData => {
    
      if(apiData && apiData.metaData && apiData.metaData.searchTerm !== this.searchTerm) return;
      
      if(apiData.message && (apiData.message.startsWith("Job for sequence") || apiData.message.startsWith("Search in progress")))
      {
        this.loadComplete = true;
        this.isSearchprogress = true;
        this.mainResultData = null;
        this.isNoresult = false;
        this.subTimeout = setTimeout(() => {
          if(this.isSequenceSearch) this.getAPIResults(searchApiUrl, auth, type, metaData);
        }, 30000);
      }else{

        if (type === 'facets') {
          if(apiData[1] !== 404){
            
            this.filterData = apiData;
            const { isUniProtReviewed, isUniProtReferenceProteome } = apiData[0].facet_fields;
            // Change the order in the dict
            let isReviewedSorted = {}
            if ('true' in isUniProtReviewed) {
              isReviewedSorted['true'] = isUniProtReviewed['true'];
            }
            if ('false' in isUniProtReviewed) {
              isReviewedSorted['false'] = isUniProtReviewed['false'];
            }
            this.filterData[0].facet_fields.isUniProtReviewed = isReviewedSorted
            // Remove those are not referenced and rename the key true to isReferenced
            // if ( 'true' in isUniProtReferenceProteome ) {
            //   Object.assign(isUniProtReferenceProteome, { isReferenced: isUniProtReferenceProteome.true })['true'];
            //   delete isUniProtReferenceProteome['true']
            // }
            if ('false' in isUniProtReferenceProteome) {
                delete isUniProtReferenceProteome['false'];
            }
            
            this.filterData[0].facet_fields.isUniProtReferenceProteome = isUniProtReferenceProteome
           
          }
        } else {
        
          const urlParamsStr = this.location.path().split('?')[1];
          if (urlParamsStr) {
            const paramsList = urlParamsStr.split('&');
            for (const paramStr of paramsList) {
              const paramArr = paramStr.split('=');
              if (paramArr[0] === 'page') {
                this.paginationData.currentPage = +(paramArr[1].trim());
              } 
            }
          }
          if (apiData && apiData.numFound > 0) {
            this.isSearchprogress = false;
            this.isNoresult = false;
            if(this.searchField === "sequence"){
              this.makeSolrRequest('facets');
              const dataWithHSP = this.getHsps(apiData.docs);
              this.mainResultData = dataWithHSP;  
            }
            else {
              this.isSequenceSearch = false;
              this.mainResultData = apiData.docs;
            }
            this.paginationData.totalPages = Math.ceil(apiData.numFound / this.paginationData.perPage);
            this.paginationData.totalRecords = apiData.numFound;
            this.paginationData.pages = this.visiblePageNumbers();

            this.paginationData = Object.assign({}, this.paginationData);
            this.mainResultData = this.mainResultData.slice();
          } else {
            this.mainResultData = null;
          }
          this.selectedItems = new Array(this.mainResultData?.length).fill(false);
          this.loadComplete = true;
        }
        this.isAllSelectedAFDB();
      }
    },
    err => {
      if(this.hasGlobalMetricValue()){
        this.isRangeFilterResultsEmpty = true
      }
      if(this.hasComplexPredictionAccuracy()){
        this.isRangeFilterResultsEmpty = true
      }
      this.isSequenceSearch = false;
      this.isSearchprogress = false;
      this.isNoresult = true;
      this.mainResultData = null;
      this.loadComplete = true;
    });
  }
  
  getCurrentUrlWithoutQueryParams(): string {
    const fullUrl = this.router.url; 
    const [path, queryParamsString] = fullUrl.split('?');
    
    const baseUrl = `${this.configService.getConfig().baseUrl}${path}`;
    
    if (!queryParamsString) {
        return baseUrl;
    }
    const queryParams = new URLSearchParams(queryParamsString);
    queryParams.delete('globalMetricValue');
    queryParams.delete('complexPredictionAccuracy_ipTM');
    queryParams.delete('complexPredictionAccuracy_ipSAE');
    queryParams.delete('complexPredictionAccuracy_pDockQ2');
    queryParams.delete('complexPredictionAccuracy_LIS');
    
    const updatedQueryParamsString = queryParams.toString();
    
    return updatedQueryParamsString ? `${baseUrl}?${updatedQueryParamsString}` : baseUrl;
  }

  makeSolrRequest(type?: string): void{
    this.isNoresult = false;
    // Show Loader
    if (type !== 'facets') { this.loadComplete = false; }

    // Set start param for pagination
    const pageStart = (this.paginationData.currentPage - 1) * this.paginationData.perPage;

    // Build queryString
    const queryTerms = this.getQueryTerms();
    const fqString = queryTerms.fq.length > 0 ? `&fq=${queryTerms.fq.join('%20AND%20')}` : '';

    // Prepare API Key
    const auth = this.configService.getConfig().apiKey ? `&key=${this.configService.getConfig().apiKey}` : '';

    // /latest hits its own dedicated endpoint (no q, no sequence variants) rather than
    // reusing the /api/search query building below.
    if (this.isLatestRoute) {
      const latestApiUrl = type === 'facets'
        ? this.buildLatestApiUrl({ type: 'multipleFacet', start: 0, rows: this.paginationData.perPage })
        : this.buildLatestApiUrl({ type: 'main', start: pageStart, rows: this.paginationData.perPage });
      this.getAPIResults(latestApiUrl, auth, type, { searchTerm: this.searchTerm, isInternal: true });
      return;
    }

    // Build API url
    const mainBaseURL =  this.configService.getConfig().searchApiUrl;

    let searchApiUrl = `${mainBaseURL}?q=${queryTerms.q}${fqString}&type=main&start=${pageStart}&rows=${this.paginationData.perPage}`;

    if(this.searchField === "sequence"){
      searchApiUrl = `${mainBaseURL}?q=${this.searchTerm.replace(/\s+/g, '')}${fqString}&type=sequence&start=${pageStart}&rows=${this.paginationData.perPage}`;
    }
    if(this.searchField === "sequence" && type === 'facets'){
      searchApiUrl = `${mainBaseURL}?q=${this.searchTerm.replace(/\s+/g, '')}${fqString}&type=sequenceMultipleFacet`;
    }

    if (this.searchField !== "sequence" && type === 'facets') {
      searchApiUrl = `${mainBaseURL}?q=${queryTerms.q}${fqString}&type=multipleFacet`;
    }
    //replacing the string with real '/'
    const newUrlWithoutSlash = searchApiUrl.replace(/__SLASH__/g, '/');
    this.getAPIResults(newUrlWithoutSlash, auth, type, { searchTerm: this.searchTerm, isInternal: true });
  }

  getHsps(docs){
    const tempDocs = [];
    docs.forEach(doc => {
      const hsp = doc["hsps"];
      if(hsp){
        const sequence_stats = this.getSequenceStats(hsp);
        const sequenceData =  {
          accession: doc.uniprotAccession,
          query_sequence: hsp.query,
          match_sequence: hsp.match,
          match_accession: hsp.subject,
          sequence_stats,
        };
        doc['sequenceData'] = sequenceData;
      }
      tempDocs.push(doc);
    });
    return docs;
  }

  getSequenceStats = (hsp) => {
    const sequenceStats = [
        {
            label: "Identity",
            value: this.getIdentities(hsp.query, hsp.match)
        },
        {
            label: "HSP score",
            value: hsp ? hsp.score : "",
        },
        {
            label: "E-value",
            value: hsp ? hsp.expect : "",
        },
        {
            label: "Positives",
            value: this.getPositives(hsp.query, hsp.match)
        },
        {
            label: "Gaps",
            value: this.getGaps(hsp.query, hsp.subject)
        }
    ];
    return sequenceStats;
  };

  getIdentities(query,match){
    const queryLength = query !== "" ? query.replace(/-/g, '').length : 0;
    const exactMatch = this.removeSpacesAndPlusSigns(match);
    const identities = exactMatch.length;
    const percentage = Math.round((identities / queryLength) * 100) //(100 * identities) / queryLength;
    
    //return identities + "/" + queryLength + " (" + percentage + "%)"; 
    return percentage + "%";
  }

  removeSpacesAndPlusSigns(match) {
    let clearmatch = "";
    for (let i = 0; i < match.length; i++) {
      let character = match[i];
      if (character !== " " && character !== "+") {
        clearmatch += character;
      }
    }
    return clearmatch;
  }

  countDashes(hit) {
    let dashCount = 0;
    for (let i = 0; i < hit.length; i++) {
      let character = hit[i];
      if (character === "-") {
        dashCount++;
      }
    }
    return dashCount;
  }

  getPositives(query,match){
    const queryLength = query !== "" ? query.replace(/-/g, '').length : 0;
    let positiveMatch = "";
    for (let i = 0; i < match.length; i++) {
      let character = match[i];
      if (character !== " ") {
        positiveMatch += character;
      }
    }
    const positives = positiveMatch.length;
    const percentage = Math.round((positives / queryLength) * 100) //(100 * positives) / queryLength;
    return positives + "/" + queryLength+ " (" + percentage + "%)"; 
  }

  getGaps(query,hit){
    const queryLength = query !== "" ? query.replace(/-/g, '').length : 0;
    const hitGaps = this.countDashes(hit);
    const queryGaps = this.countDashes(query);
    const gaps = hitGaps + queryGaps;
    const percentage = Math.round((gaps / queryLength) * 100) //(100 * gaps) / queryLength;
    return gaps + "/" + queryLength+ " (" + percentage + "%)";
  }

  async facetActionInterface (){
    this.isFilterListOpen = false;
    const filterKey = this.filterKeyMap[this.dropdownSelectedFilter];
    const filters = this.selectedFilters[filterKey];
    let isChecked = true;

    // Clamp min and max to allowed limits using constants
    const minLimit = this.interfaceMinLimit;
    let min = Number(this.minInterface);
    let max = Number(this.maxInterface);

    // Swap if min > max
    if (min > max) {
      [min, max] = [max, min];
    }

    // Clamp min to minLimit
    if (min < minLimit) {
      min = minLimit;
    }
    // Clamp max to 1
    if (max > 1) {
      max = 1;
    }

    // Update UI to reflect clamped values
    this.minInterface = min;
    this.maxInterface = max;

    if (this.minInterface !== 0 || this.maxInterface !== 1) {
      // 1. Get the current active key (e.g., 'complexPredictionAccuracy_ipTM')
      const currentActiveKey = this.filterKeyMap[this.dropdownSelectedFilter];
      // --- FIX DUPLICATES: Clear the array for this specific key before pushing ---
      this.selectedFilters[currentActiveKey] = [];
      // 2. Add only the NEW range to the filter list
      const rangeValue = `[${this.minInterface}+TO+${this.maxInterface}]`;
      this.selectedFilters[currentActiveKey].push(rangeValue);
      // 3. Prepare parameters for the URL update
      const paramsToUpdate: any = {
        [currentActiveKey]: rangeValue
      };
      // 4. Nullify any other accuracy params currently in the URL
      Object.keys(this.route.snapshot.queryParams).forEach(key => {
        if (key.startsWith('complexPredictionAccuracy_') && key !== currentActiveKey) {
          paramsToUpdate[key] = null;
          }
      });

      // 5. Update the URL
      this.router.navigate([], {
          relativeTo: this.route,
          queryParams: paramsToUpdate,
          queryParamsHandling: 'merge'
      });

      // 6. Refresh the data
      this.getQueryTerms();
      this.resetPagination();
    
       this.executeSearch();
    }else {
      // 1. Identify accuracy params to remove
      const paramsToClear: any = {};
      Object.keys(this.route.snapshot.queryParams).forEach(key => {
        if (key.startsWith('complexPredictionAccuracy_')) {
          paramsToClear[key] = null;
        }
      });

      // 2. Navigate (This triggers the ngOnInit subscription automatically)
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: paramsToClear,
        queryParamsHandling: 'merge'
      }).then(() => {
        // 3. After navigation is done, refresh the data
        this.resetPagination();
        this.executeSearch();
      });
    }
    this.gaService.eventEmitter('search_filter_click', 'search', 'click', 'quality_score', 'clicks on quality filter Apply range ')
    this.gaService.eventEmitter('search_quality_score', 'search', 'click', `${this.dropdownSelectedFilter}`, 'When user selects a quality score')
  }

  // Helper to keep the API call consistent in one place
  executeSearch() {
    this.getQueryTerms();
    this.makeSolrRequest('main');
    if (this.searchField !== "sequence") {
      this.makeSolrRequest('facets');
    }
  }

  facetActionPlddt (){
    this.isFilterListOpen = false;
    const filters = this.selectedFilters['globalMetricValue'];
   
    let isChecked = true;
    // if (filters.length !== 0) {
    //   isChecked = false;
    //   filters.length = 0; // Clears the array
    // }
    
    if (Number(this.minplddt) > Number(this.maxplddt)) {
      let temp = this.minplddt;
      this.minplddt = this.maxplddt;
      this.maxplddt = temp;
      
    }
    if (Number(this.minplddt) < 0 || Number(this.maxplddt) < 0){
      this.minplddt = Math.abs(this.minplddt);
      this.maxplddt = Math.abs(this.maxplddt);
    }

    if (Number(this.maxplddt) > 100){
      this.maxplddt = 100;
    }
    filters.push(`[${this.minplddt}+TO+${this.maxplddt}]`);
    
    this.updateBrowserLink('globalMetricValue', `[${this.minplddt}+TO+${this.maxplddt}]`, isChecked);
    this.getQueryTerms();
    this.resetPagination();
   
    if(this.searchField === "sequence"){
      this.makeSolrRequest('main');
      //this.localStorageSearchTerm = localStorage[params.term];
    }else{
      this.makeSolrRequest('main');
      this.makeSolrRequest('facets');
    }

    this.gaService.eventEmitter('search_filter_click', 'search', 'click', 'quality_score', 'clicks on quality filter Apply range ')
    this.gaService.eventEmitter('search_quality_score', 'search', 'click', 'pLDDT', 'When user selects a quality score pLDDT')
  
  }
  facetAction(selectedFacet: string, facetValue: string, isChecked: boolean): void {
    this.isFilterListOpen = false;
    const filters = this.selectedFilters[selectedFacet];

    const toggleFilter = () => {
      if (isChecked) {
        filters.push(facetValue);
      } else {
        filters.splice(filters.indexOf(facetValue), 1);
      }
    };
    toggleFilter();

    this.updateBrowserLink(selectedFacet, facetValue, isChecked);
    this.getQueryTerms();
    this.resetPagination();
   
    if(this.searchField === "sequence"){
      this.makeSolrRequest('main');
      //this.localStorageSearchTerm = localStorage[params.term];
    }else{
      this.makeSolrRequest('main');
      this.makeSolrRequest('facets');
    }

    this.gaService.eventEmitter('search_filter_click', 'search', 'click', "search_proteome_filter", undefined)
  }

  facetActionLink(selectedFacet: string, facetValue: string): void {
    let isChecked: boolean;

    if (selectedFacet === 'oligomericStateDescription') {
      // Single-select: only one oligomericStateDescription value can be active at a time.
      const alreadyActive = this.selectedFilters[selectedFacet].includes(facetValue);
      if (alreadyActive) {
        // Toggle off
        this.selectedFilters[selectedFacet] = [];
        isChecked = false;
        this.oligomericStateDescription = 'initial';
      } else {
        // Replace any previously active value with the new selection
        this.selectedFilters[selectedFacet] = [facetValue];
        isChecked = true;
        this.oligomericStateDescription = facetValue;
      }
    } else {
      const filters = this.selectedFilters[selectedFacet];
      isChecked = true;
      if (filters.includes(facetValue)) {
        filters.splice(filters.indexOf(facetValue), 1);
        isChecked = false;
      } else {
        filters.push(facetValue);
      }
    }

    this.updateBrowserLink(selectedFacet, facetValue, isChecked);
    this.resetPagination();
    // this.makeSolrRequest('main');
    // this.makeSolrRequest('facets');

    if(this.searchField === "sequence"){
      this.makeSolrRequest('main');
      //this.localStorageSearchTerm = localStorage[params.term];
    }else{
      this.makeSolrRequest('main');
      this.makeSolrRequest('facets');
    }
    
    let tag = facetValue
    if ( selectedFacet === 'isUniProtReviewed') {
      tag = facetValue === 'true' ? 'search_reviewed_filter' : 'search_unreviewed_filter'
      this.gaService.eventEmitter('search_filter_click', 'search', 'click', tag, undefined);
    }
    if ( selectedFacet === 'organismScientificName') {
      tag = "search_popular_filter"
      this.gaService.eventEmitter('search_filter_click', 'search', 'click', tag, undefined);
    }

    //Entry type and dataset filter GA tags
    const facetTagMap: { [key: string]: { [value: string]: string } } = {
      oligomericStateDescription: {
        _filter: 'entry_type',
        'Monomer': 'Monomers',
        '!Monomer': 'Multimers',
        'Homodimer': 'Homodimers',
        'Heterodimer': 'Heterodimers'
      },
      providerId: {
        _filter: 'dataset_filter',
        'NVIDIA': 'NVDA',
        'Viro3D': 'viro3d',
        'Google Deepmind': 'GDM',
        'AllTheBacteria': 'atbc',
        'Big Fantastic Virus Database': 'bfvd',
        'Kinetoplastid': 'kinetoplastid'
      }
    };

    // Entry type filter GA tags
    if (selectedFacet === 'oligomericStateDescription') {
      this.gaService.eventEmitter('search_filter_click', 'search', 'click', 'entry_type', undefined);
      
      const tag = facetTagMap.oligomericStateDescription[facetValue];
      if (tag) {
        this.gaService.eventEmitter('search_entry_type_filter', 'search', 'click', tag, undefined);
      }
    }

    // Dataset filter GA tags
    if (selectedFacet === 'providerId') {
      this.gaService.eventEmitter('search_filter_click', 'search', 'click', 'dataset_filter', 'click on any dataset filter');
      
      if (facetTagMap.providerId[facetValue]) {
        this.gaService.eventEmitter('search_dataset_filter', 'search', 'click', facetValue, undefined);
      }
    }
  }
  isFacetActive(facetField: string, facetValue: string): boolean {
    const filters = this.selectedFilters[facetField];
    return filters.includes(facetValue)
  }

  areFiltersActives(): boolean {
    return  Object.values(this.selectedFilters).some(array => array.length > 0);
  }


  updateBrowserLink(selectedFacet: string, facetValue: string, isChecked: boolean): void {
    
    const urlAndParamData = this.getUrlAndParams();
    let params: Params = {};
    if (isChecked) {
      // urlAndParamData.queryParams[selectedFacet] = facetValue;
      params[selectedFacet] = facetValue
    } else {
      // delete urlAndParamData.queryParams[selectedFacet];
      params[selectedFacet] = null;
    }
    delete urlAndParamData.queryParams.page;
    this.router.navigate([urlAndParamData.locPath], { queryParams: params, queryParamsHandling: 'merge' });
  }

  resetPagination(): void {
    this.paginationData = {
      perPage: 20, currentPage: 1, totalPages: 0, pages: [], totalRecords: 0
    };
  }


  getUrlAndParams(): any {
    // On the /latest route there are no search path segments to reconstruct
    if (this.isLatestRoute) {
      return { locPath: '/latest', queryParams: {} };
    }

    // The path (field + term) must never be rebuilt from this.searchTerm here: for a
    // portal category (see resolveSearchTerm) searchTerm is the backend's raw tag code
    // (e.g. 'PPP'), not the readable URL slug the user is actually looking at (e.g.
    // 'pandemic-preparedness') — reconstructing the path from it silently replaced the
    // friendly URL with the raw tag code every time a filter, page, or "clear filters"
    // click ran (AFPDBE-1418). None of those three actions ever change the search
    // itself, only filters/pagination, so just keep the path exactly as it already is
    // in the address bar and only touch query params.
    // location.path() is already percent-encoded, but router.navigate([...]) encodes each
    // segment itself — decode first or multi-word terms get double-encoded (%20 → %2520).
    const [pathOnly, prevParams] = this.location.path().split('?');
    const locPath = decodeURIComponent(pathOnly);

    const urlAndParamData: any = {
      locPath,
      queryParams: {}
    };

    if (prevParams) {
      const paramsList = prevParams.split('&');
      for (const paramStr of paramsList) {
        const paramArr = paramStr.split('=');

        if (paramArr[0] === 'page') {
          urlAndParamData.queryParams['page'] = +(paramArr[1].trim());
        } else if (this.filters[paramArr[0]] > -1) {
          urlAndParamData.queryParams[paramArr[0]] = decodeURIComponent(paramArr[1]).trim();
        }

      }
    }
    return urlAndParamData;
  }

  visiblePageNumbers(): any[] {
    const innerWindow = 1;
    const outerWindow = 0;
    let windowFrom = this.paginationData.currentPage - innerWindow;
    let windowTo = this.paginationData.currentPage + innerWindow;

    // If the window is truncated on one side, make the other side longer
    if (windowTo > this.paginationData.totalPages) {
      windowFrom = Math.max(0, windowFrom - (windowTo - this.paginationData.totalPages));
      windowTo = this.paginationData.totalPages;
    }
    if (windowFrom < 1) {
      windowTo = Math.min(this.paginationData.totalPages, windowTo + (1 - windowFrom));
      windowFrom = 1;
    }

    let visible = [];

    // Always show the first page
    visible.push(1);
    // Don't add inner window pages twice
    for (let i = 2; i <= Math.min(1 + outerWindow, windowFrom - 1); i++) {
      visible.push(i);
    }
    // If the gap is just one page, close the gap
    if (1 + outerWindow == windowFrom - 2) {
      visible.push(windowFrom - 1);
    }
    // Don't add the first or last page twice
    for (var i = Math.max(2, windowFrom); i <= Math.min(windowTo, this.paginationData.totalPages - 1); i++) {
      visible.push(i);
    }
    // If the gap is just one page, close the gap
    if (this.paginationData.totalPages - outerWindow == windowTo + 2) {
      visible.push(windowTo + 1);
    }
    // Don't add inner window pages twice
    for (let i = Math.max(this.paginationData.totalPages - outerWindow, windowTo + 1); i < this.paginationData.totalPages; i++) {
      visible.push(i);
    }
    // Always show the last page, unless it's the first page
    if (this.paginationData.totalPages > 1) {
      visible.push(this.paginationData.totalPages);
    }

    var links = [];

    let prev = null;

    for (let i = 0, l = visible.length; i < l; i++) {
      if (prev && visible[i] > prev + 1) {
        links.push(-1);
      }
      links.push(visible[i]);
      prev = visible[i];
    }
    return links;
  }
  paginateTo(paginate: { source: string, pageIndex: number }): void {
    // Handle pagination logic
    if (paginate.source === 'arrow') {
      if (paginate.pageIndex === -1 && this.paginationData.currentPage === 1) return;
      if (paginate.pageIndex === 1 && this.paginationData.currentPage === this.paginationData.totalPages) return;
      this.paginationData.currentPage = this.paginationData.currentPage + paginate.pageIndex;
    } else {
      if (this.paginationData.currentPage === paginate.pageIndex) return;
      this.paginationData.currentPage = paginate.pageIndex;
    }

    // Reset select-all and initialize page selections if not already set
    this.currentPage = this.paginationData.currentPage;
    this.downloadService.updateAllSelected(false);
    this.initializePageSelections(this.currentPage);

    // Populate selectedItems based on current page’s saved selections
    this.selectedItems = [...this.pageSelections[this.currentPage]];
    this.checkSelectionLimit();

    this.paginationData.pages = this.visiblePageNumbers();
    this.paginationData = Object.assign({}, this.paginationData);
    this.makeSolrRequest('main');

    // Push URL to maintain browser history
    const urlAndParamData = this.getUrlAndParams();
    urlAndParamData.queryParams['page'] = this.paginationData.currentPage;
    this.router.navigate([urlAndParamData.locPath], { queryParams: urlAndParamData.queryParams, queryParamsHandling: 'merge' });
  }

  initializePageSelections(pageIndex: number) {
    // Initialize the selection array for this page if not yet created
    if (!this.pageSelections[pageIndex]) {
      this.pageSelections[pageIndex] = new Array(this.PAGE_SIZE).fill(false);
    }
  }

  updatePerPageVal(ppgSelected: any): void {

    // Reset to page 1
    this.paginationData.currentPage = 1;
    this.paginationData.perPage = ppgSelected.ppgValue;
    this.paginationData.pages = this.visiblePageNumbers();
    this.paginationData = Object.assign({}, this.paginationData);

    // Make solr request
    this.makeSolrRequest('main');
  }

  getResultCountText(): string{

    let title = '0 results';
    if (this.paginationData.totalRecords > 0) {
      const ppVal = this.paginationData.perPage;
      const fromVal = ((this.paginationData.currentPage - 1) * ppVal) + 1;
      let toVal = ((this.paginationData.currentPage - 1) * ppVal) + ppVal;
      if (this.paginationData.currentPage == this.paginationData.totalPages){
        toVal = this.paginationData.totalRecords;
      }
      title = `${fromVal} - ${toVal} of ${this.paginationData.totalRecords} results`;
    }
    return title;
  }

  // urlTerm is the route's raw :term segment (a readable slug, e.g.
  // "pandemic-preparedness"), not the backend's thematic tag code.
  resolvePortalCategory(searchField: string, urlTerm: string): PortalCategory | undefined {
    if (searchField !== 'thematic_tags') {
      return undefined;
    }
    return DEFAULT_CATEGORIES.find(category => category.categorySlug === urlTerm);
  }

  // The actual search/filter query always uses the resolved category's real
  // backend tag code (e.g. PPP), never the readable URL slug. Falls back to
  // the raw URL term for any non-portal-category search (unchanged today),
  // or for an old/unmatched thematic_tags URL (so it still runs a search
  // instead of breaking, just without the friendly title/banner).
  resolveSearchTerm(urlTerm: string, category: PortalCategory | undefined): string {
    return category ? category.thematicTag : urlTerm;
  }

  toggleFilterList(): void {
    const filterListEle = this.el.nativeElement.querySelector('.filterList');
    filterListEle.style.height = 'auto';
    if (this.isFilterListOpen) { filterListEle.style.height = '0'; }
    this.isFilterListOpen = !this.isFilterListOpen;
  }

  ngOnDestroy(): void {
    this.locSubscriber?.unsubscribe();
    this.routeSubscriber?.unsubscribe();
    this.minplddt = 0;
    this.maxplddt = 100;
  }
  getDictLength(dictionary: Record<string, unknown> | null | undefined): number {
    return dictionary ? Object.keys(dictionary).length : 0;
  }

  existField(key: string): boolean {
    return this.getDictLength(this.filterData?.[0]?.facet_fields?.[key]) > 0;
  }

  //Returns true if there are 2 or more states (e.g., monomer AND dimer)
  hasMultipleOligomericStates(): boolean {
    const state = this.filterData[0]?.facet_fields?.oligomericStateDescription;
    
    if (!state) return false;

    const keys = Object.keys(state);
    return keys.length > 1 || (keys.length === 1 && keys[0] !== 'Monomer');
  }

  clearFilters() {
    const filters = Object.keys(this.selectedFilters);
    filters.forEach(key => this.selectedFilters[key] = []);
    const urlAndParamData = this.getUrlAndParams();
    // delete urlAndParamData.queryParams.page;
    this.resetPagination();
    // this.makeSolrRequest('main');
    // this.makeSolrRequest('facets');

    if(this.searchField === "sequence"){
      this.makeSolrRequest('main');
      //this.localStorageSearchTerm = localStorage[params.term];
    }else{
      this.makeSolrRequest('main');
      this.makeSolrRequest('facets');
    }
    this.minplddt = 0;
    this.maxplddt = 100;
    this.oligomericStateDescription = 'initial';
    this.providerId = 'initial';
    this.router.navigate([urlAndParamData.locPath]);
  }

  isReferenceOnclick(isUniProtReferenceProteome: string) {
    this.gaService.eventEmitter('search_filter_click', 'search', 'click', 'isUniProtReferenceProteome', undefined)
  }

  hasGlobalMetricValue() {
    const data = this.selectedFilters;
    return Array.isArray(data.globalMetricValue) && data.globalMetricValue.length > 0;
  }
  //udpate the below function to check if any of the complex prediction accuracy filters are applied

  hasComplexPredictionAccuracy() {
    for (const key in this.filterKeyMap) {
      if (this.filterKeyMap.hasOwnProperty(key)) {
        const filterName = this.filterKeyMap[key];
        const filter = this.selectedFilters[filterName];
        if (Array.isArray(filter) && filter.length > 0) {
          return true;
        }
      }
    }
    return false;
  }

  // copySequence(sequence) {
  //   navigator.clipboard.writeText(sequence);
  //   this.isCopySequence = true;
  //   setTimeout(() => {this.isCopySequence = false;}, 5000);
  // }

  copySequence(sequence: string) {
    if (isPlatformBrowser(this.platformId)) {
      navigator.clipboard.writeText(sequence).then(() => {
        this.isCopySequence = true;
        setTimeout(() => {
          this.isCopySequence = false;
        }, 5000);
      }).catch(err => {
        console.error('Clipboard write failed:', err);
      });
    }
  }

  copyLink() {
    if (isPlatformBrowser(this.platformId)) {
      const link = window.location.href;
      navigator.clipboard.writeText(link).then(() => {
        this.isCopyLink = true;
        setTimeout(() => {
          this.isCopyLink = false;
        }, 5000);
      }).catch(err => {
        console.error('Clipboard write failed:', err);
      });
    }
  }
}
function takeUntilDestroyed(destroyRef: any): import("rxjs").OperatorFunction<void, unknown> {
  throw new Error('Function not implemented.');
}

